"use client";

import Link from "next/link";
import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { useTranslations } from "next-intl";
import { capturePostHogException } from "@/lib/posthog-capture";
import { recoverFromChunkLoadError } from "@/lib/chunk-load-recovery";
import {
  recoverFromClerkFrontendLoadError,
  retryAfterClerkOrChunkError,
} from "@/lib/clerk-load-recovery";

export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations("Errors");
  useEffect(() => {
    capturePostHogException(error, {
      source: "next_app_error_boundary",
      error_digest: error.digest,
    });
    recoverFromChunkLoadError(error);
    recoverFromClerkFrontendLoadError(error);
  }, [error]);

  return (
    <main className="mx-auto flex min-h-[90vh] max-w-lg flex-col items-center justify-center px-6 text-center">
      {/* Icon */}
      <div
        className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl"
        style={{
          background: "rgba(201,168,76,0.1)",
          border: "1px solid rgba(201,168,76,0.2)",
        }}
      >
        <AlertTriangle className="h-8 w-8" style={{ color: "#c9a84c" }} />
      </div>

      {/* Label */}
      <p
        className="mb-3 text-[11px] font-bold uppercase tracking-[0.13em]"
        style={{ color: "#c9a84c" }}
      >
        {t("somethingWentWrong")}
      </p>

      {/* Heading */}
      <h1
        className="mb-3 font-serif text-[36px] font-black leading-[1.1] tracking-[-0.025em]"
        style={{ color: "#faf6ee" }}
      >
        {t("unexpectedBefore")}{" "}
        <em className="italic" style={{ color: "#c9a84c" }}>
          {t("unexpectedGold")}
        </em>{" "}
        {t("unexpectedAfter")}
      </h1>

      {/* Body */}
      <p
        className="mb-8 text-[15px] leading-relaxed"
        style={{ color: "rgba(250,246,238,0.55)" }}
      >
        {t("captured")}
      </p>

      {/* CTAs */}
      <div className="flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={() => {
            retryAfterClerkOrChunkError(error, reset);
          }}
          className="cursor-pointer rounded-full px-6 py-3 text-sm font-semibold transition-opacity hover:opacity-90 active:opacity-75"
          style={{
            background: "linear-gradient(135deg, #c9a84c, #e8c96a)",
            color: "#0e0c0a",
          }}
        >
          {t("tryAgain")}
        </button>
        <Link
          href="/parties"
          className="cursor-pointer rounded-full px-6 py-3 text-sm font-semibold transition-opacity hover:opacity-80"
          style={{
            border: "1px solid rgba(250,246,238,0.18)",
            color: "rgba(250,246,238,0.75)",
          }}
        >
          {t("goToParties")}
        </Link>
      </div>
    </main>
  );
}
